/* @flow */

import log from 'loglevel';
import * as React from 'react';

import type { BasicFunction, ReadOnlyKeyValuePair } from '../../helpers/jsHelpers/types';
import type { Profile } from '../../helpers/profiles/typesAndConstants';
import type { SettingValue } from '../../helpers/settings/types';
import type { DmsSettingMap } from '../../libs/netgemLibrary/dms/types/DeviceInfoSettings';
import type { NETGEM_API_V8_FEED_ITEM } from '../../libs/netgemLibrary/v8/types/FeedItem';
import type { NETGEM_API_V8_HUB } from '../../libs/netgemLibrary/v8/types/Hub';
import type { PIN_DATA_MODAL_TYPE } from '../modal/pinModal/PinModalConstsAndTypes';
import type { ShakaOfflineContent } from '../player/implementation/shakaTypes';

import { APPLICATION_ID } from '../../helpers/applicationCustomization/types';
import { Setting, type Settings } from '../../helpers/settings/types';
import { AvenueType, type TitledAvenue } from '../../helpers/ui/avenue/types';
import { ExternalContentDisplayType } from '../../redux/appConf/types/types';

export const ONE_HUNDRED = 100;
export const TWO = 2;
export const ONE_MEGABYTE = 1_048_576;

// When refreshing offline content, UI is disabled for this amount in order to give a visual feedback to user (in ms)
export const OFFLINE_CONTENT_REFRESH_DELAY = 300;

// Each menu item has a height of 38px (OpenSans) in all apps except FranceChannel, 34px (Roboto)
const OPEN_SANS_LINE_HEIGHT = 38;
const ROBOTO_LINE_HEIGHT = 34;
export const MENU_ITEM_HEIGHT: number = process.env.REACT_APP_ID === APPLICATION_ID.FranceChannel ? ROBOTO_LINE_HEIGHT : OPEN_SANS_LINE_HEIGHT;

// First menu item text is start at 8px
export const MENU_ITEM_START = 8;

// Default values for time-related slider (in seconds)
export const DEFAULT_TIME_SLIDER = Object.freeze({
  Max: 120,
  Min: 0,
  Step: 1,
});

export const MENU_ITEMS_NO_PROFILES: Array<string> = Object.freeze(['settings.general.title', 'settings.content.title', 'settings.audio_video.title', 'settings.more.title', 'settings.debug.title']);

export const MENU_ITEMS_PROFILES: Array<string> = Object.freeze(['settings.profiles.title', 'settings.general.title', 'settings.more.title', 'settings.debug.title']);

export const INDEX_NO_PROFILES: ReadOnlyKeyValuePair<number> = Object.freeze({
  AudioVideo: 2,
  Content: 1,
  Debug: 4,
  General: 0,
  More: 3,
});

export const INDEX_PROFILES: ReadOnlyKeyValuePair<number> = Object.freeze({
  Debug: 3,
  General: 1,
  More: 2,
  Profiles: 0,
});

export const LOG_LEVELS: ReadonlyArray<{ text: string, value: number }> = Object.freeze([
  { text: 'Trace', value: log.levels.TRACE },
  { text: 'Debug', value: log.levels.DEBUG },
  { text: 'Info', value: log.levels.INFO },
  { text: 'Warning', value: log.levels.WARN },
  { text: 'Error', value: log.levels.ERROR },
]);

type SliderSettingUI = {
  labelRenderer?: (value: number) => string,
  max: number,
  min: number,
  step: number,
  valueRenderer?: (value: number) => string,
};

export type SliderSetting = {
  ...SliderSettingUI,
  id: string,
  isExpertMode?: boolean,
  label: string,
  picto?: React.Node,
  setting: Setting,
  validate?: (value: number) => boolean,
  valueConverter?: (value: number) => number,
  valueConverterBack?: (value: number) => number,
};

export const DefaultTimeSliderValues: SliderSettingUI = {
  max: DEFAULT_TIME_SLIDER.Max,
  min: DEFAULT_TIME_SLIDER.Min,
  step: DEFAULT_TIME_SLIDER.Step,
};

export type ReduxSettingsReducerStateType = {
  +deviceSettings: DmsSettingMap,
  +didomiApiKey: string,
  +didomiNoticeId: string,
  +guruWebsiteUrl: string,
  +isDebugModeEnabled: boolean,
  +isNpvrFeatureEnabled: boolean,
  +isRegisteredAsGuest: boolean,
  +isSubscriberIdShown: boolean,
  +isSuperWifiEnabled: boolean,
  +logoutUrl: string,
  +mainAvenue: ?TitledAvenue,
  +settings: Settings,
  +subscriberId: string,
  +userAccountDisplay: ExternalContentDisplayType,
  +userAccountUrl: string,
  +versionApp: string,
  +versionBO: string,
  +versionCrmBack: string,
  +versionCrmFront: string,
};

export type ReduxSettingsDispatchToPropsType = {
  +localHideModal: BasicFunction,
  +localNavigateToAvenue: (index: number, type: AvenueType, hubItem?: NETGEM_API_V8_FEED_ITEM, hub?: NETGEM_API_V8_HUB, searchString?: string) => void,
  +localSendLogoutRequest: (logoutUrl?: string) => Promise<void>,
  +localShowKeyboardShortcuts: BasicFunction,
  +localShowParentalControlModal: BasicFunction,
  +localShowPinModal: (data: PIN_DATA_MODAL_TYPE) => void,
  +localShowProfileEditorModal: (modalId: string, profileId?: string) => void,
  +localShowProfilesModal: BasicFunction,
  +localShowProfileSplashscreenModal: BasicFunction,
  +localShowSettingsModal: BasicFunction,
  +localToggleDebugMode: BasicFunction,
  +localUpdateSetting: (setting?: Setting, value: SettingValue) => void,
};

export type AppSettingsPropType = {
  +initialSection: ?string,
};

export type CompleteAppSettingsPropType = {
  ...AppSettingsPropType,
  ...ReduxSettingsReducerStateType,
  ...ReduxSettingsDispatchToPropsType,
};

export type AppSettingsStateType = {
  currentProfile: Profile | null,
  isDeletingAllOfflineContent: boolean,
  isParentalControlEnabled: boolean,
  isRefreshingOfflineContent: boolean,
  logLevel: number,
  offlineContent: Array<ShakaOfflineContent>,
  offlineContentDeleting: Set<string>,
  pinCodeExists: boolean,
  profileRefreshKey: number,
  selectedMenuItem: number,
};
